import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useCallback } from "react";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useMutationWithError } from "@/utils/hooks/useMutationWithError";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { ClubStackNavigatorParamList } from "../../../../../../navigators/ClubNavigator";
import { bookingSummaryAnalyticsDataAtom } from "../../BookingSummary/atoms/analytics/bookingSummaryAnalyticsDataAtom";
import type {
  DeleteDiningBookingInput,
  useCancelDiningReservationMutation,
} from "./__generated__/useCancelDiningReservationMutation.graphql";

type CancelDiningReservationArgs = {
  input: DeleteDiningBookingInput;
};

export const useCancelDiningReservation = () => {
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const { showToast } = useToast();
  const analyticsData = useAtomValue(bookingSummaryAnalyticsDataAtom);
  const trackEvent = useTrackEvent();

  const [commit, isLoading] =
    useMutationWithError<useCancelDiningReservationMutation>(
      useCancelDiningReservationMutationString
    );

  const cancelDiningReservation = useCallback(
    ({ input }: CancelDiningReservationArgs) => {
      commit({
        variables: { input },
        onCompleted: (res) => {
          if (res?.responsePayload?.success) {
            trackEvent("BookingDetail.SlotBookingDeleted", analyticsData ?? {});
            navigation.navigate("Home", { screen: "MainHome" });
            showToast({
              description: t`Booking cancelled.`,
              semanticStyle: "success",
              toastId: "cancel-dining-booking-success",
            });
          }
        },
        onCompletedErrorCustom: (errors) => {
          navigation.navigate("Home", { screen: "MainHome" });
          showToast({
            description: t`Sorry. An error occurred while cancelling your booking.`,
            semanticStyle: "error",
            toastId: "cancel-dining-booking-error",
          });
          return [];
        },
      });
    },
    [analyticsData, commit, navigation, showToast, trackEvent]
  );

  return [cancelDiningReservation, isLoading] as const;
};

const useCancelDiningReservationMutationString = graphql`
  mutation useCancelDiningReservationMutation(
    $input: DeleteDiningBookingInput!
  ) {
    responsePayload: deleteDiningBooking(input: $input) {
      success
      errors {
        code

        message
        field
      }
    }
  }
`;
